/**
 * Permission checking utilities.
 * Reads from authState.permissions (populated after login + tenant select).
 */
import { computed } from 'vue'
import { authState } from './authState'

export function hasPermission(permission) {
  return authState.permissions.includes(permission)
}

export function hasAllPermissions(perms) {
  return perms.every(p => authState.permissions.includes(p))
}

export function hasAnyPermission(perms) {
  return perms.some(p => authState.permissions.includes(p))
}

export function hasAdminAccess() {
  return authState.permissions.some(p => p.startsWith('admin:'))
}

/**
 * Vue composable for reactive permission checks.
 */
export function usePermissions() {
  const permissions = computed(() => authState.permissions)

  const can = (perm) => computed(() => hasPermission(perm))
  const canAny = (perms) => computed(() => hasAnyPermission(perms))
  const canAll = (perms) => computed(() => hasAllPermissions(perms))

  return {
    permissions,
    can,
    canAny,
    canAll,
    // Pre-computed for common checks
    canReadFunds: computed(() => hasPermission('funds:read')),
    canWriteFunds: computed(() => hasPermission('funds:write')),
    canReadInvestments: computed(() => hasPermission('investments:read')),
    canWriteInvestments: computed(() => hasPermission('investments:write')),
    canReadFinancials: computed(() => hasPermission('financials:read')),
    canWriteFinancials: computed(() => hasPermission('financials:write')),
    canReadUploads: computed(() => hasPermission('uploads:read')),
    canWriteUploads: computed(() => hasPermission('uploads:write')),
    canApproveUploads: computed(() => hasPermission('uploads:approve')),
    canReadReports: computed(() => hasPermission('reports:read')),
    canExportReports: computed(() => hasPermission('reports:export')),
    canManageUsers: computed(() => hasPermission('admin:users:manage')),
    canManageSettings: computed(() => hasPermission('admin:settings:manage')),
    isAdmin: computed(() => hasAdminAccess())
  }
}

/**
 * v-permission directive.
 * Usage: v-permission="'funds:read'" or v-permission="['funds:read', 'funds:write']"
 * Modifiers: .any (OR check), .disable (disable instead of hide)
 */
export const permissionDirective = {
  mounted(el, binding) {
    updateVisibility(el, binding)
  },
  updated(el, binding) {
    updateVisibility(el, binding)
  }
}

function updateVisibility(el, binding) {
  const perms = Array.isArray(binding.value) ? binding.value : [binding.value]
  const useAny = binding.modifiers.any
  const useDisable = binding.modifiers.disable

  const allowed = useAny ? hasAnyPermission(perms) : hasAllPermissions(perms)

  if (!allowed) {
    if (useDisable) {
      el.setAttribute('disabled', 'disabled')
      el.classList.add('disabled')
    } else {
      el.style.display = 'none'
    }
  } else {
    if (useDisable) {
      el.removeAttribute('disabled')
      el.classList.remove('disabled')
    } else {
      el.style.display = ''
    }
  }
}
